import { useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { CustomKnowledgeRecord } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { useTranslation } from '@frontend/i18n';
import { SurfaceHeader } from '@frontend/surface-header';
import { Button, TextField, TextareaField, Text, Tray, useForm } from '@frontend/design-system';
import { useAIRCustomKnowledgeStore } from '../../../../stores/air-settings.store';

type Props = {
  show: boolean;
  onClose: () => void;
  editItem?: CustomKnowledgeRecord | null;
  locationId: string;
};

const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_NAME_LENGTH = 250;

export const CustomKnowledgeSidePanel = ({ show, onClose, editItem, locationId }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  const { draftSettings, mutateDraft } = useAIRCustomKnowledgeStore(['draftSettings', 'mutateDraft']);
  const nameFieldRef = useRef<HTMLInputElement>(null);

  const { getFieldProps, values, seedValues, isComplete, reset } = useForm({
    fields: {
      name: {
        type: 'text',
        value: editItem?.customKnowledgeTitle || '',
        required: true,
      },
      description: {
        type: 'text',
        value: editItem?.customKnowledgeContent || '',
        required: false,
        validateOnChange: true,
        validator: () => '',
      },
    },
  });

  const isDuplicate = useMemo(() => {
    const val = (values.name as string) || '';
    if (!val.trim()) return false;

    const items = draftSettings[locationId]?.records || [];
    return items.some((item) => {
      if (editItem && item.id === editItem.id) {
        return false;
      }
      return item.customKnowledgeTitle?.toLowerCase() === val.toLowerCase();
    });
  }, [values.name, draftSettings, locationId, editItem]);

  useEffect(() => {
    if (editItem) {
      seedValues({
        name: editItem.customKnowledgeTitle || '',
        description: editItem.customKnowledgeContent || '',
      });
    }
  }, [editItem, show, seedValues]);

  const nameFieldProps = getFieldProps('name');
  const descriptionFieldProps = getFieldProps('description');

  const handleInsertOrUpdate = () => {
    const nameValue = (values.name as string) || '';
    const descriptionValue = (values.description as string) || '';

    if (!nameValue.trim()) return;
    if (nameFieldProps.error || isDuplicate) return;

    const itemId = editItem ? editItem.id : `custom-${Date.now()}`;
    const newItem: CustomKnowledgeRecord = {
      id: itemId,
      customKnowledgeTitle: nameValue,
      customKnowledgeContent: descriptionValue,
    };

    mutateDraft(locationId, (draft) => {
      if (!draft.records) {
        draft.records = [];
      }
      const existingIndex = draft.records.findIndex((item) => item.id === itemId);
      if (existingIndex > -1) {
        draft.records[existingIndex] = newItem;
      } else {
        draft.records.push(newItem);
      }
    });

    onClose();
  };

  useEffect(() => {
    if (show) {
      nameFieldRef.current?.focus();
    }

    return () => {
      reset();
    };
  }, [show]);

  return (
    <Tray
      css={{
        overflowY: 'scroll',
        padding: 0,
        gap: 0,
      }}
      width='large'
      show={show}
      onClose={onClose}
      showBackdrop={true}
      mountTarget='[data-settings-modal-content]'
    >
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Custom Knowledge')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <Button variant='secondary' iconName='x' onClick={onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>

      <div css={styles.panelContent}>
        <div css={styles.formContainer}>
          <div css={styles.row}>
            <div css={{ flex: 1 }}>
              <TextField
                {...nameFieldProps}
                error={isDuplicate ? t('Title must be unique') : nameFieldProps.error}
                aria-invalid={!!nameFieldProps.error || isDuplicate}
                label={t('Name')}
                placeholder={t('Type Something...')}
                maxLength={MAX_NAME_LENGTH}
                ref={nameFieldRef}
              />
            </div>
          </div>
          <div css={{ marginTop: 'var(--fabric-space-gap-md)' }}>
            <TextareaField
              {...descriptionFieldProps}
              label={t('Description')}
              placeholder={t('Type Something...')}
              name='description'
              maxLength={MAX_DESCRIPTION_LENGTH}
              autoGrow={[1, 10]}
            />
            <div css={styles.charCounter}>
              <Text size='small' color='subdued'>
                {((values.description as string) || '').length} / {MAX_DESCRIPTION_LENGTH}
              </Text>
            </div>
          </div>
        </div>

        <div css={styles.footer}>
          <Text size='small' color='subdued'>
            {editItem
              ? t("Clicking 'Update' will only update the knowledge not save it.")
              : t("Clicking 'Insert' will only add the knowledge not save it.")}
          </Text>
          <Button disabled={!isComplete || isDuplicate} onClick={handleInsertOrUpdate}>
            {editItem ? t('Update') : t('Insert')}
          </Button>
        </div>
      </div>
    </Tray>
  );
};

const styles = {
  panelContent: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: var(--fabric-color-surface-inset-default);
  `,
  formContainer: css`
    padding: var(--fabric-space-padding-lg);
    background-color: var(--fabric-color-surface-default);
    border-radius: var(--fabric-radius-md);
    margin: var(--fabric-space-gap-md);
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
  `,
  row: css`
    display: flex;
    gap: var(--fabric-space-gap-md);
    align-items: flex-start;
  `,
  charCounter: css`
    display: flex;
    justify-content: flex-end;
    margin-top: var(--fabric-space-gap-xxs);
  `,
  footer: css`
    margin-top: auto;
    padding: var(--fabric-space-padding-md) var(--fabric-space-padding-lg);
    border-top: 1px solid var(--fabric-color-border-subdued);
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--fabric-color-surface-default);
  `,
};
